import { useState } from 'react'

import { useFormContext } from 'react-hook-form'
import { useTranslation } from 'react-i18next'

import type { TDetalhesPropostaSchema } from '@/features/proposta/brasil/domain/schemas/detalhes-proposta-schema'
import { useListarImobiliariasCorretores } from '@/features/proposta/brasil/hooks/use-imobiliarias-corretores'
import { CardImobiliariaCorretorBase } from '@/features/proposta/shared/components/card-imobiliaria-corretor-base'
import {
  FORMAT_OPTIONS_PARTICIPACAO_MONETARIO,
  FORMAT_OPTIONS_PARTICIPACAO_PERCENTUAL,
} from '@/features/proposta/shared/utils/constants'

const configuracaoNumberInputPor = {
  percentual: {
    labelKey: 'detalhes_proposta.imobiliaria.inputs.label_percentual' as const,
    formatOptions: FORMAT_OPTIONS_PARTICIPACAO_PERCENTUAL,
    field: 'percentualCorretagemImobiliaria' as const,
  },
  valor: {
    labelKey: 'detalhes_proposta.imobiliaria.inputs.label_valor' as const,
    formatOptions: FORMAT_OPTIONS_PARTICIPACAO_MONETARIO,
    field: 'valorCorretagemImobiliaria' as const,
  },
}

type TCardImobiliariaCorretorBrasilProps = {
  index: number
  onExcluirParticipacao: VoidFunction
  onSelecionarImobiliaria: (
    participacaoPath: `detalhesDaProposta.imobiliariasCorretores.participacoes.${number}`,
  ) => void
}

export const CardImobiliariaCorretorBrasil = ({
  index,
  onExcluirParticipacao,
  onSelecionarImobiliaria,
}: TCardImobiliariaCorretorBrasilProps) => {
  const { t } = useTranslation('proposta')
  const { control, watch } = useFormContext<TDetalhesPropostaSchema>()
  const [query, setQuery] = useState('')
  const { data, refetch, isLoading, isError, fetchNextPage, hasNextPage, isFetchingNextPage } =
    useListarImobiliariasCorretores({ descricao: query, tamanhoDaPagina: 20 })

  const participacaoPath = `detalhesDaProposta.imobiliariasCorretores.participacoes.${index}` as const
  const imobiliariaCorretor = watch(`${participacaoPath}`)

  const { field, formatOptions, labelKey } = configuracaoNumberInputPor[imobiliariaCorretor.corretagem.tipo]

  return (
    <CardImobiliariaCorretorBase
      control={control}
      onExcluirParticipacao={onExcluirParticipacao}
      imobiliaria={{
        name: `${participacaoPath}.imobiliariaCorretor`,
        data: data?.pages.flatMap((page) => page.records) ?? [],
        labelKey: 'descricao',
        label: 'Imobiliária',
        onSearch: setQuery,
        onSelect: () => onSelecionarImobiliaria(participacaoPath),
        isLoading,
        isError,
        retry: refetch,
        isLoadingMore: isFetchingNextPage,
        onEndReach: hasNextPage ? fetchNextPage : undefined,
      }}
      participacao={{
        name: `${participacaoPath}.corretagem.${field}`,
        label: t(labelKey),
        formatOptions,
      }}
    />
  )
}
